import { ChangeDetectorRef, Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Client } from '@df/core/service/client';
import { getElementHeight } from '@df/dom/fn/get-element-height';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subscription } from 'rxjs';

@Directive()
export abstract class ReadMoreComponentDef implements OnInit, OnChanges, OnDestroy {
    /**
     * Full text to show
     *
     * BINDING
     * @type {string}
     */
    @Input()
    text?: string;

    /**
     * Character limit to apply
     * IF NOT SET WE WILL CHECK OVEFLOW
     *
     * BINDING
     * @type {string}
     */
    @Input()
    limit?: string;

    /**
     * If full content is visible flag
     * @type {boolean}
     */
    isMoreVisible = ['prerender', 'legacyPrerender'].includes(this.client.mode);

    /**
     * Initial part of text always visible
     * @type {string}
     */
    begining?: string;

    /**
     * Followin part of text visible in full mode only
     * @type {string}
     */
    rest?: string;

    /**
     * Last character limit in overflow mode
     * @type {number}
     */
    protected lastLimit: number = 0;

    /**
     * Numbe of tries to consider no-overflow as valid result
     * @type {number}
     */
    protected tryNum = 0;

    protected subscriptions = new Subscription();

    constructor(
        protected timeoutService: TimeoutService,
        protected changeDetectorRef: ChangeDetectorRef,
        public elementRef: ElementRef,
        private client: Client
    ) {
        this.changeDetectorRef.detach();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.client.obs.resize.subscribe(
                this.timeoutService.debounce(() => this.timeoutService.setTimeout(() => this.update(), 50), 100)
            )
        );
        this.timeoutService.setTimeout(() => this.check(false), 50);

        if (this.client.isPrerender) {
            this.toggle(true);
        }
    }

    ngOnChanges(changesObj: SimpleChanges) {
        if (changesObj['text']) {
            this.isMoreVisible = false;
        }
        this.update();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Toggles isMoreVisible flag
     * @param  {boolean = !this.isMoreVisible} to  optional flag value to be set
     */
    toggle(to = !this.isMoreVisible): void {
        this.timeoutService.setTimeout(() => {
            this.isMoreVisible = to;
            this.changeDetectorRef.detectChanges();
        }, 50);
    }

    /**
     * Applies limits and prepares beginning and rest variagles
     */
    public update(): void {
        const limitString = this.limit;
        // character limit mode
        if (limitString) {
            const limit = parseInt(limitString, 10);
            this.applyLimit(limit);
        } else {
            // overflow mode
            this.check(true);
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Used with overflow mode, checks if text overflows container
     * @param  {boolean = false} resetFlag
     */
    public check(resetFlag = false): void {
        if (resetFlag) {
            this.lastLimit = this.text ? this.htmlToPlaintext(this.text).length : 0;
            this.tryNum = 0;
        }

        if (!this.limit && this.tryNum < 20) {
            const ov = this.checkOverflow();

            if (ov > 1) {
                const newLimit = Math.min(this.lastLimit - 10, Math.floor(this.lastLimit / ov) + 10);

                this.lastLimit = newLimit;
                this.applyLimit(newLimit);
            } else {
                this.tryNum++;
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Applies limit of given number
     * @param  {number} limit [description]
     * @return {string}       [description]
     */
    protected applyLimit(limit: number): string | undefined {
        const plainText = this.text ? this.htmlToPlaintext(this.text) : '';

        if (plainText.length < limit) {
            this.begining = undefined;
            this.rest = undefined;
            return plainText;
        }

        let begining = this.text?.substring(0, limit).replace(/\s[^\s]+$/, '') || ''; // to last space bar
        const lastOpenTag = begining.lastIndexOf('<');
        const lastCloseTag = begining.lastIndexOf('>');

        if (lastOpenTag > lastCloseTag) {
            begining = begining.substring(0, lastOpenTag);
        }

        this.begining = begining;
        this.rest = this.text?.substring(this.begining.length) || '';

        return;
    }

    /**
     * Checks if beginnig text is causing overflow
     * @return {number} Returns child/parent ratio - >1 means overflow
     */
    protected checkOverflow(): number {
        this.begining = this.text;

        const parent = this.elementRef.nativeElement as HTMLElement;
        const child = parent.querySelector('.read-more__beginning');
        const parentHeight = getElementHeight(parent) || 1;
        const childHeight = (child && getElementHeight(child)) || 0;

        return childHeight / parentHeight;
    }

    protected htmlToPlaintext(text: string): string {
        return text ? String(text).replace(/<[^>]+>/gm, '') : '';
    }
}
